import React from 'react';
import { User, GraduationCap, Cpu, Award, Briefcase, Sparkles, CheckCircle2 } from 'lucide-react';
import { AboutPhotoCarousel } from './AboutPhotoCarousel';

export const AboutSection: React.FC = () => {
  return (
    <section id="about" className="about" style={{ padding: '6rem 0', position: 'relative' }}>
      <div className="container">
        {/* Section Header */}
        <div className="section-title-wrap">
          <div className="section-tag">
            <User size={14} />
            <span>Personal Profile</span>
          </div>
          <h2 className="section-title">
            About <span>Me</span>
          </h2>
          <p className="section-subtitle">
            An Entry-Level IT Graduate dedicated to continuous learning, building practical web applications, and delivering technical value to forward-thinking teams.
          </p>
        </div>

        {/* About Grid Layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '3rem',
            alignItems: 'center',
            marginTop: '2rem'
          }}
          className="about-grid"
        >
          {/* Column 1: Narrative & Mindset */}
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              padding: '2.2rem',
              backdropFilter: 'var(--blur-glass)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.2rem' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'rgba(0, 242, 254, 0.12)',
                  border: '1px solid rgba(0, 242, 254, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-cyan)'
                }}
              >
                <Sparkles size={20} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-title)' }}>
                  Hi, I’m Lorenz Saldivar
                </h3>
                <span style={{ fontSize: '0.85rem', color: 'var(--accent-cyan)', fontWeight: 600 }}>
                  BSIT Graduate (Class of 2026) • Web Dev & IT Support
                </span>
              </div>
            </div>

            <p style={{ color: 'var(--text-muted)', lineHeight: '1.75', fontSize: '0.98rem', marginBottom: '1.2rem' }}>
              I’m a recent Bachelor of Science in Information Technology graduate from <strong>Tagoloan Community College (Class of 2026)</strong>, originally from <strong>Pongol, Libona, Bukidnon</strong>. I specialize in <strong>Web Development</strong> and <strong>IT Technical Support</strong>. As a fresh graduate, I am highly motivated to dive into the industry, take on hands-on opportunities to enhance my technical foundations, and explore new technologies.
            </p>

            <p style={{ color: 'var(--text-muted)', lineHeight: '1.75', fontSize: '0.98rem', marginBottom: '1.5rem' }}>
              Beyond the screen, I stay disciplined and energized through the <strong>gym, hiking, and running</strong>. I bring that same endurance and problem-solving mindset—whether I am hitting the trails or troubleshooting complex IT systems and building clean web experiences.
            </p>

            {/* Core Values Bullets */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                <CheckCircle2 size={16} color="var(--accent-cyan)" />
                <span><strong>AI-Assisted Workflow:</strong> Leveraging Claude Code, AntiGravity & Codex for rapid development</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                <CheckCircle2 size={16} color="var(--accent-cyan)" />
                <span><strong>Full-Stack Practical Mindset:</strong> React, TypeScript, PHP/Laravel, MySQL & Supabase</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                <CheckCircle2 size={16} color="var(--accent-cyan)" />
                <span><strong>IT Systems & Hardware:</strong> Network troubleshooting, OS maintenance & technical support</span>
              </div>
            </div>
          </div>

          {/* Column 2: Highlights Info Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            {/* Card 1 */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '1.2rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.2rem',
                transition: 'transform 0.3s ease'
              }}
              className="about-info-card"
            >
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: 'rgba(0, 242, 254, 0.12)',
                  color: 'var(--accent-cyan)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <GraduationCap size={24} />
              </div>
              <div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                  Educational Institution
                </span>
                <h4 style={{ fontSize: '1.05rem', color: 'var(--card-heading)', fontWeight: 700, marginTop: '2px' }}>
                  Tagoloan Community College
                </h4>
                <p style={{ fontSize: '0.83rem', color: 'var(--text-dim)' }}>
                  BS In Information Technology (BSIT)
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '1.2rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.2rem'
              }}
              className="about-info-card"
            >
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: 'rgba(127, 0, 255, 0.12)',
                  color: 'var(--accent-violet)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Award size={24} />
              </div>
              <div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                  Published Capstone Research
                </span>
                <h4 style={{ fontSize: '1.05rem', color: 'var(--card-heading)', fontWeight: 700, marginTop: '2px' }}>
                  Lumichat Mental Health Chatbot
                </h4>
                <p style={{ fontSize: '0.83rem', color: 'var(--text-dim)' }}>
                  Backend Developer • Published in IJERT Journal
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '1.2rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.2rem'
              }}
              className="about-info-card"
            >
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: 'rgba(0, 230, 118, 0.12)',
                  color: 'var(--accent-green)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Briefcase size={24} />
              </div>
              <div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                  Industry Internship
                </span>
                <h4 style={{ fontSize: '1.05rem', color: 'var(--card-heading)', fontWeight: 700, marginTop: '2px' }}>
                  Speakify Teams Corp.
                </h4>
                <p style={{ fontSize: '0.83rem', color: 'var(--text-dim)' }}>
                  IT Technical Support & Admin Data Specialist (3 Months)
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '1.2rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.2rem'
              }}
              className="about-info-card"
            >
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: 'rgba(245, 158, 11, 0.12)',
                  color: 'var(--accent-gold)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Cpu size={24} />
              </div>
              <div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                  AI Engineering Approach
                </span>
                <h4 style={{ fontSize: '1.05rem', color: 'var(--card-heading)', fontWeight: 700, marginTop: '2px' }}>
                  Claude Code, AntiGravity & Codex
                </h4>
                <p style={{ fontSize: '0.83rem', color: 'var(--text-dim)' }}>
                  Accelerated development & continuous skill enhancement
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* About Personal Photo Gallery Carousel */}
        <AboutPhotoCarousel />
      </div>
    </section>
  );
};
